"use client";
import * as React from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import type * as SelectPrimitive from "@radix-ui/react-select";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect } from "react";
import type { OptionType } from "@/types";
import { useDismissOnOutside } from "@/hooks/use-dismiss-on-outside";
import type { DismissReason } from "@/hooks/use-dismiss-on-outside";
import { useDropdownPlacement } from "@/hooks/use-dropdown-placement";
// import Distance from "@/model/distance";

/** Search box plus the 400px scroller — the height the flip decision is measured against. */
const PANEL_PREFERRED_HEIGHT = 460;

// Custom SingleSelect component
const SingleSelect = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
    placeholder?: string;
    data: OptionType[];
    defaultOption?: OptionType | undefined;
    searchEnabled?: boolean;
    readValueFor?: string;
    selectedOption?: OptionType;
    onSelect?: (item: OptionType) => void;
  }
>(({ placeholder, children: _children, ...props }, ref) => {
  const {
    data,
    searchEnabled = true,
    //readValueFor = "name",
    onSelect,
    defaultOption,
    selectedOption,
    disabled = false,
  } = props;
  const [searchQuery, setSearchQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  // The option the keyboard is on. Focus stays in the search box; this is surfaced to
  // assistive tech through aria-activedescendant.
  const [activeIndex, setActiveIndex] = React.useState(-1);
  // `getDisplayText` below only ever reads `selectedOption` through this —
  // without seeding from it too, a caller that renders with `selectedOption`
  // set but never passes `defaultOption` (e.g. pre-filling a dropdown from an
  // already-applied filter) shows the placeholder instead of that value.
  const [selectedValue, setSelectedValue] = React.useState<
    OptionType | undefined
  >(defaultOption ?? selectedOption);

  // Explicitly nullable so useRef hands back a MutableRefObject — setContainerRef below
  // assigns to .current, which the RefObject overload marks read-only.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const baseId = React.useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const { placement, maxHeight } = useDropdownPlacement(triggerRef, open, {
    preferredHeight: PANEL_PREFERRED_HEIGHT,
  });

  // Keep the forwarded ref working while still owning a node reference for outside-click
  // detection.
  const setContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof ref === "function") ref(node as never);
      else if (ref) (ref as React.MutableRefObject<unknown>).current = node;
    },
    [ref],
  );

  // Filter data based on search query
  const filteredData = React.useMemo(() => {
    if (!searchQuery.trim()) return data;
    return data.filter((option: OptionType) =>
      option?.displayName?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, data]);

  const close = React.useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleDismiss = React.useCallback(
    (reason: DismissReason) => {
      close();
      // Only reclaim focus for Escape — on an outside click the user has already moved on.
      if (reason === "escape") triggerRef.current?.focus();
    },
    [close],
  );

  useDismissOnOutside(containerRef, open, handleDismiss);

  // Handle value change
  const handleValueChange = (value: OptionType) => {
    setSearchQuery("");
    setSelectedValue(value);
    onSelect?.(value);
  };

  const selectAt = (index: number) => {
    const item = filteredData[index];
    if (!item) return;
    close();
    handleValueChange(item);
    triggerRef.current?.focus();
  };

  // Get display text for the trigger
  const getDisplayText = () => {
    if (!selectedValue) return placeholder || "Select an option";
    const selected = data.find(
      (s) =>
        s.displayName ===
        (selectedOption
          ? selectedOption.displayName
          : selectedValue.displayName),
    );
    return selected ? selected.displayName : placeholder || "Select an option";
  };

  const moveActive = (step: number) => {
    if (!filteredData.length) return;
    const next =
      activeIndex < 0
        ? step > 0
          ? 0
          : filteredData.length - 1
        : activeIndex + step;
    if (next < 0 || next >= filteredData.length) return;
    setActiveIndex(next);
  };

  const openPanel = (index: number) => {
    setOpen(true);
    setActiveIndex(index);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (open) {
      // With no search box there is nowhere else for focus to go, so the trigger drives the
      // list too. When the search box is present it holds focus and this never fires.
      handleListKeyDown(event);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openPanel(event.key === "ArrowDown" ? 0 : data.length - 1);
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        moveActive(-1);
        return;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        return;
      case "End":
        event.preventDefault();
        setActiveIndex(filteredData.length - 1);
        return;
      case "Enter":
        event.preventDefault();
        if (activeIndex >= 0) selectAt(activeIndex);
        return;
      case "Tab":
        close();
        return;
      default:
    }
  };

  // Move focus into the panel so the arrow keys have somewhere to land.
  useEffect(() => {
    if (open && searchEnabled) searchRef.current?.focus();
  }, [open, searchEnabled]);

  // A caller can disable this once options run out from under an already-open
  // panel (e.g. a filter narrowing what's left to zero) — a disabled trigger
  // takes no more clicks, but that alone wouldn't close a panel already open.
  useEffect(() => {
    if (disabled && open) close();
  }, [disabled, open, close]);

  // Keep the keyboard-active option in view as the arrows walk past the scroll edge.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const option = listRef.current?.children[activeIndex];
    // jsdom does not implement scrollIntoView, hence the optional call.
    (option as HTMLElement | undefined)?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  return (
    <div className="relative" ref={setContainerRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open && !searchEnabled && activeIndex >= 0
            ? optionId(activeIndex)
            : undefined
        }
        disabled={disabled}
        className="flex h-9 w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setOpen(!open)}
        onKeyDown={handleTriggerKeyDown}
      >
        <div className="flex-1 truncate">{getDisplayText()}</div>
        <ChevronDown aria-hidden="true" className="size-4 opacity-50" />
      </button>
      {open && (
        <div
          className={cn(
            // flex-col + the inline maxHeight below let the scroller shrink instead of the
            // panel overflowing the space the placement hook allowed it.
            "absolute z-50 flex w-full min-w-32 flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80",
            placement === "top" ? "bottom-full mb-1" : "top-full mt-1",
          )}
          style={{ maxHeight }}
        >
          {searchEnabled ? (
            <div className="p-2">
              {/* The input drops its own ring to sit flush in this box, so the box shows focus. */}
              <div className="flex items-center rounded-md border px-2 focus-within:ring-1 focus-within:ring-ring">
                <Search aria-hidden="true" className="mr-2 size-4 opacity-50" />
                <Input
                  ref={searchRef}
                  role="combobox"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-activedescendant={
                    activeIndex >= 0 ? optionId(activeIndex) : undefined
                  }
                  aria-label="Search options"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setActiveIndex(-1);
                  }}
                  onKeyDown={handleListKeyDown}
                  className="h-8 border-0 text-sm font-medium focus-visible:ring-0 focus-visible:ring-offset-0"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          ) : null}
          <ScrollArea className="max-h-[400px] min-h-0 flex-1">
            <div className="p-1" id={listboxId} role="listbox" ref={listRef}>
              {filteredData.map((item: OptionType, index: number) => (
                <div
                  key={index}
                  id={optionId(index)}
                  role="option"
                  aria-selected={
                    selectedOption?.displayName === item.displayName
                  }
                  className={cn(
                    "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-primary-extraLight hover:text-accent-foreground",
                    selectedValue === item.id
                      ? "bg-accent text-accent-foreground"
                      : "",
                    index === activeIndex ? "bg-primary-extraLight" : "",
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={(e) => {
                    e.stopPropagation();
                    // close(), not setOpen(false) — otherwise the keyboard-active row stays
                    // highlighted the next time the panel opens.
                    close();
                    handleValueChange(item);
                  }}
                >
                  <span className="absolute left-2 flex size-3.5 items-center justify-center">
                    {selectedOption?.displayName === item.displayName ? (
                      <Check className="size-4" />
                    ) : null}
                  </span>
                  {item.displayName}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
});
SingleSelect.displayName = "SingleSelect";

export default SingleSelect;
